// features/hive/ui/HiveChartSection/HiveChartSection.tsx
import {
  ChartAbsoluteBlock,
  ChartAbsoluteCol,
  ChartAbsoluteFooter,
  ChartAbsoluteHeader,
  ChartAbsoluteMinMaxCol,
  ChartAbsoluteMinMaxRow,
  ChartContainer,
  ChartHeader,
  ChartMarkCreate,
  ChartMarkFooter,
  ChartMarkInput,
  ChartMarkInputDesc,
  ChartMarkItem,
  ChartMarkItemColor,
  ChartMarkItemHeader,
  ChartMarkItemMore,
  ChartMarkItemTitleRow,
  ChartMarksContainer,
  ChartMarksTitleRow,
  ChartMarkWrapper,
  ChartRow,
  ChartSwitchRow,
  ChartWrapper,
  DateInputChartWrapper,
} from 'features/hive/ui/HiveChartSection/styles.ts';
import {Input, InputDate, Segment, SegmentedControl, Switch} from '@atomaro/ui-kit';
import Select from '@atomaro/ui-kit/components/Select/Select';
import {useMemo, useState, type ReactElement, useEffect, useCallback} from 'react';
import {useApiariesStore} from 'entities/apiaries';
import {useParams} from 'react-router-dom';
import {DefaultChartWidget} from 'widgets/chart';
import {hiveChartsMocks} from 'features/hive/ui/HiveBodyAnalytics/mocks';
import {chartValue, LegendItem, XTickFormatter} from 'widgets/chart/model/types';
import {AnalyticsCard} from 'features/hive/ui/HiveBodyAnalytics/AnalyticsCard';
import {UIPeriod, PeriodKey, ScaleKey} from 'features/hive/ui/HiveBodyAnalytics/HiveBodyAnalytics';
import {HappyChapter} from 'shared/ui/icons/chapterIcons/happyChapter';
import TemperatureChapter from 'shared/ui/icons/chapterIcons/temperatureChapter';
import HumidityChapter from 'shared/ui/icons/chapterIcons/humidityChapter';
import Co2Chapter from 'shared/ui/icons/chapterIcons/co2Chapter';
import WeightChapter from 'shared/ui/icons/chapterIcons/weightChapter';
import StrengthChapter from 'shared/ui/icons/chapterIcons/strengthChapter';
import {useOutletColorStore} from 'entities/outletColor/model/store.ts';
import {MoreIcon} from 'shared/ui/icons/moreIcon.tsx';
import {AddLarge} from 'shared/ui/icons';
import {HiveMarkActions} from 'features/hive/ui/HiveChartSection/HiveMarkActions.tsx';
import HiveMarkEdit, {
  HiveMarkEditPayload,
} from 'features/hive/ui/HiveChartSection/HiveMarkEdit.tsx';
import {marksColors} from 'features/hive/ui/HiveChartSection/consts.ts';
import {COLORS as MHZ_COLORS} from 'shared/consts';
import {withMask} from 'use-mask-input';
import {COLORS as COLORS_THEME} from '../../../../shared/consts';
import {toast} from 'react-toastify';
import {$api} from 'shared/api';
import {SensorTypeEnum} from 'shared/types';
import {UnionButton} from 'features/hive/ui/HiveTitleRight/styles';
import {UnionIcon} from 'shared/ui/icons';
import {useCurrentHiveStore} from 'entities/currentHive';

type ChartId = 'happiness' | 'temperature' | 'humidity' | 'co2' | 'weight' | 'state';

const SENSOR_TYPE_BY_CHART: Partial<Record<ChartId, SensorTypeEnum>> = {
  temperature: SensorTypeEnum.IN_TEMPERATURE,
  humidity: SensorTypeEnum.IN_HUMIDITY,
  co2: SensorTypeEnum.CO2,
  weight: SensorTypeEnum.WEIGHT,
};

const COLORS = {
  marks: '#6B8AFF',
  bar: 'var(--accent-muted, rgba(255, 114, 65, 1))',
  bar2: 'var(--neutral-muted, rgba(181, 183, 192, 1))',
  line: 'var(--accent-default, rgba(255, 79, 18, 1))',
  diffPos: 'var(--accent-default, rgba(255, 79, 18, 1))',
  diffNeg: COLORS_THEME.BG.SURFACE3,
  threshold: '#FF4F12',
};

const allowedScales: Record<PeriodKey, ScaleKey[]> = {
  today: ['hours'],
  yesterday: ['hours'],
  week: ['hours', 'days'],
  month: ['hours', 'days', 'weeks'],
  year: ['days', 'weeks', 'months'],
  custom_1_2_days: ['hours'],
  custom_3_7_days: ['hours', 'days'],
  custom_8_46_days: ['hours', 'days', 'weeks'],
  custom_47_plus: ['days', 'weeks', 'months'],
};

const PERIOD_SCALE_MAP: Partial<Record<PeriodKey, ScaleKey>> = {
  today: 'hours',
  yesterday: 'hours',
  week: 'hours',
  month: 'days',
  year: 'months',
  custom_1_2_days: 'hours',
  custom_3_7_days: 'hours',
  custom_8_46_days: 'days',
  custom_47_plus: 'months',
};

const HOUR_MS = 3600_000;
const DAY_MS = 24 * HOUR_MS;

const RU_MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
] as const;

const TICK_RULES: Partial<
  Record<
    PeriodKey,
    | {type: 'fixed'; interval: number; alignToDay?: boolean}
    | {type: 'sixHourlyAligned'}
    | {type: 'quarter'}
    | {type: 'daily'}
    | {type: 'weekly'}
  >
> = {
  today: {type: 'sixHourlyAligned'},
  yesterday: {type: 'sixHourlyAligned'},
  week: {type: 'daily'},
  month: {type: 'fixed', interval: 7 * DAY_MS},
  year: {type: 'quarter'},
  custom_1_2_days: {type: 'sixHourlyAligned'},
  custom_3_7_days: {type: 'daily'},
  custom_8_46_days: {type: 'fixed', interval: 7 * DAY_MS},
  custom_47_plus: {type: 'quarter'},
};

const buildSixHourlyAlignedTicks = (start: number, end: number) => {
  const ticks: number[] = [];
  const startDate = new Date(start);
  const endDate = new Date(end);
  const pushUnique = (value: number) => {
    if (ticks.length === 0 || ticks[ticks.length - 1] !== value) {
      ticks.push(value);
    }
  };
  pushUnique(start);
  // Начинаем с 00:00 текущих суток
  const alignedStart = new Date(startDate);
  alignedStart.setHours(0, 0, 0, 0);

  let current = alignedStart.getTime();

  while (current <= endDate.getTime()) {
    if (current >= start) {
      pushUnique(current);
    }
    current += 6 * HOUR_MS;
  }

  // Добавляем последнюю точку, если её нет
  if (ticks.length === 0 || ticks[ticks.length - 1] < end) {
    pushUnique(end);
  }

  return ticks;
};

const aggregateToDaily = (data: {date: string; value: number}[]) => {
  if (!data || data.length === 0) return [];

  const dailyMap = new Map<string, {sum: number; count: number}>();

  data.forEach((point) => {
    const date = new Date(point.date);
    const dayKey = date.toISOString().split('T')[0]; // "YYYY-MM-DD"

    if (!dailyMap.has(dayKey)) {
      dailyMap.set(dayKey, {sum: 0, count: 0});
    }

    const dayData = dailyMap.get(dayKey)!;
    dayData.sum += point.value;
    dayData.count += 1;
  });

  const result = Array.from(dailyMap.entries())
    .map(([date, {sum, count}]) => ({
      date: `${date}T00:00:00.000Z`,
      value: count > 0 ? sum / count : 0,
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return result;
};

function classifyCustomRangeDays(days: number): PeriodKey {
  return days <= 7 ? 'week' : 'month';
}

function toSeries(arr?: {date: string; value: number}[] | undefined): chartValue[] {
  return (arr ?? []).map((p) => ({x: new Date(p.date).getTime(), y: p.value}));
}

function lastTimeStr(data?: {date: string; value: number}[]): string {
  if (!data || data.length === 0) return '';
  const last = new Date(data[data.length - 1].date);
  const hh = String(last.getHours()).padStart(2, '0');
  const mm = String(last.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function avgValue(data?: {date: string; value: number}[]): number {
  if (!data || data.length === 0) return 0;
  const s = data.reduce((acc, p) => acc + (p?.value ?? 0), 0);
  return Number((s / data.length).toFixed(1));
}

function periodDelta(data?: {date: string; value: number}[]): number {
  if (!data || data.length < 2) return 0;
  const first = data[0]?.value ?? 0;
  const last = data[data.length - 1]?.value ?? 0;
  return Number((last - first).toFixed(1));
}

function legendSeries(label: string, color: string): LegendItem {
  return {label, color};
}

const alignStartOfDay = (ms: number) => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

type CustomRange = {start: number; end: number};

const normalizeRange = (start: Date, end: Date): CustomRange => {
  const startDate = new Date(start);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(end);
  endDate.setHours(23, 59, 59, 999);
  return {start: startDate.getTime(), end: endDate.getTime()};
};

const buildFixedIntervalTicks = (
  start: number,
  end: number,
  interval: number,
  alignToDay?: boolean
) => {
  if (!isFinite(start) || !isFinite(end) || interval <= 0) return undefined;
  let current = alignToDay ? alignStartOfDay(start) : start;
  if (current > start) current -= interval;
  const ticks: number[] = [];
  while (current <= end) {
    ticks.push(current);
    current += interval;
  }
  if (ticks.length === 0 || ticks[ticks.length - 1] < end) {
    ticks.push(end);
  }
  return ticks;
};

const buildQuarterTicks = (start: number, end: number) => {
  if (!isFinite(start) || !isFinite(end)) return undefined;
  const ticks: number[] = [];
  const cursor = new Date(start);
  cursor.setDate(1);
  cursor.setHours(0, 0, 0, 0);
  const quarterStart = Math.floor(cursor.getMonth() / 3) * 3;
  cursor.setMonth(quarterStart);
  while (cursor.getTime() <= end) {
    ticks.push(cursor.getTime());
    cursor.setMonth(cursor.getMonth() + 3);
  }
  if (ticks.length === 0 || ticks[ticks.length - 1] < end) {
    ticks.push(new Date(end).setHours(0, 0, 0, 0));
  }
  return ticks;
};

const startOfMonthTs = (ts: number) => {
  const d = new Date(ts);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const addMonthsTs = (ts: number, months: number) => {
  const d = new Date(ts);
  d.setMonth(d.getMonth() + months);
  return d.getTime();
};

const buildYearTicks = (start: number) => {
  if (!isFinite(start)) return undefined;
  const startMonth = startOfMonthTs(start);
  const endMonth = addMonthsTs(startMonth, 12);
  const ticks: number[] = [startMonth];

  const cursor = new Date(startMonth);
  cursor.setDate(1);
  cursor.setHours(0, 0, 0, 0);
  const month = cursor.getMonth();
  const monthsToNextQuarter = (3 - (month % 3)) % 3 || 3;
  cursor.setMonth(month + monthsToNextQuarter);

  while (cursor.getTime() < endMonth) {
    ticks.push(cursor.getTime());
    cursor.setMonth(cursor.getMonth() + 3);
  }

  ticks.push(endMonth);
  return ticks;
};

const buildDailyTicks = (start: number, end: number) => {
  if (!isFinite(start) || !isFinite(end)) return undefined;
  const ticks: number[] = [start];
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  if (cursor.getTime() <= start) {
    cursor.setDate(cursor.getDate() + 1);
  }
  while (cursor.getTime() < end) {
    ticks.push(cursor.getTime());
    cursor.setDate(cursor.getDate() + 1);
  }
  if (ticks[ticks.length - 1] !== end) {
    ticks.push(end);
  }
  return ticks;
};

const buildXTicksForSeries = (
  series: chartValue[] | undefined,
  period: PeriodKey,
  override?: CustomRange | null
) => {
  const start = override?.start ?? series?.[0]?.x;
  const end = override?.end ?? series?.[series.length - 1]?.x;
  if (!start || !end) return undefined;

  const rule = TICK_RULES[period];
  if (!rule) return undefined;

  if (period === 'year') {
    return buildYearTicks(start);
  }

  if (rule.type === 'quarter') {
    return buildQuarterTicks(start, end);
  }
  if (rule.type === 'daily') {
    return buildDailyTicks(start, end);
  }
  if (rule.type === 'fixed') {
    return buildFixedIntervalTicks(start, end, rule.interval, rule.alignToDay);
  }

  if (rule.type === 'sixHourlyAligned') {
    return buildSixHourlyAlignedTicks(start, end);
  }
  // return buildFixedIntervalTicks(start, end, rule.interval, rule.alignToDay)
  return undefined;
};

const formatTickPartsForPeriod = (
  period: PeriodKey,
  ms: number,
  index?: number,
  ticks?: number[]
): string[] => {
  const d = new Date(ms);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const baseDate = `${dd}.${mm}`;
  if (period === 'today' || period === 'yesterday' || period === 'custom_1_2_days') {
    return [`${hh}:00`];
  }
  if (period === 'week' || period === 'custom_3_7_days') {
    return [baseDate];
  }
  if (period === 'month' || period === 'custom_8_46_days') {
    return [baseDate];
  }
  if (period === 'year') {
    const isEdge =
      typeof index === 'number' && ticks ? index === 0 || index === ticks.length - 1 : false;
    if (isEdge) {
      return [RU_MONTHS[d.getMonth()]];
    }
    const quarter = Math.floor(d.getMonth() / 3) + 1;
    return [`${quarter} кв ${d.getFullYear()}`];
  }
  if (period === 'custom_47_plus') {
    const quarter = Math.floor(d.getMonth() / 3) + 1;
    return [`${quarter} квартал`, String(d.getFullYear())];
  }
  return [baseDate];
};

const getScaleForPeriod = (period: PeriodKey): ScaleKey => {
  return PERIOD_SCALE_MAP[period] ?? allowedScales[period]?.[0] ?? 'hours';
};

type RemotePoint = {
  time?: string;
  timestamp?: string;
  date?: string;
  value?: number | string | null;
};
type HiveAnalyticsResponse = Partial<
  Record<'temperature' | 'humidity' | 'co2' | 'weight', RemotePoint[]>
>;

type AnalyticsInterval = 'hour' | 'day' | 'week' | 'month';

const startOfDay = (date: Date) => {
  const clone = new Date(date);
  clone.setHours(0, 0, 0, 0);
  return clone;
};

const getRequestRange = (uiPeriod: UIPeriod, customRange: CustomRange | null) => {
  if (uiPeriod === 'custom' && customRange) {
    const from = new Date(customRange.start);
    const to = new Date(customRange.end);
    const diffDays = Math.max(1, Math.ceil((customRange.end - customRange.start) / DAY_MS));
    const interval: AnalyticsInterval =
      diffDays <= 2 ? 'hour' : diffDays <= 60 ? 'day' : diffDays <= 365 ? 'week' : 'month';
    return {from, to, interval};
  }

  const now = new Date();
  let to = new Date(now);
  let from = new Date(now);

  switch (uiPeriod) {
    case 'today':
      from = new Date(now.getTime() - 24 * HOUR_MS);
      break;
    case 'yesterday': {
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      from = yesterday;
      to = new Date(from);
      to.setDate(from.getDate() + 1);
      to.setMilliseconds(to.getMilliseconds() - 1); // 23:59:59.999
      break;
    }
    case 'week':
      from = new Date(now.getTime() - 7 * DAY_MS);
      break;
    case 'month':
      from = new Date(now.getTime() - 30 * DAY_MS);
      break;
    case 'year':
      from = new Date(now.getTime() - 365 * DAY_MS);
      break;
    default:
      from = startOfDay(now);
      break;
  }

  const diffDays = Math.max(1, Math.ceil((to.getTime() - from.getTime()) / DAY_MS));
  let interval: AnalyticsInterval = 'day';
  if (diffDays <= 2) interval = 'hour';
  else if (diffDays > 60 && diffDays <= 365) interval = 'week';
  else if (diffDays > 365) interval = 'month';

  return {from, to, interval};
};

const mapRemoteSeries = (arr?: RemotePoint[]) =>
  (arr ?? [])
    .map((point) => {
      const date = point.time ?? point.timestamp ?? point.date ?? null;
      const value = point.value ?? 0;
      if (!date) return null;
      const num = typeof value === 'number' ? value : Number(value ?? 0);
      return {date, value: Number.isFinite(num) ? num : 0};
    })
    .filter((item): item is {date: string; value: number} => !!item);

const parseDateMs = (value?: string | null) => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
};

const filterSeriesFrom = <T extends {date: string; value: number}>(
  data: T[] | undefined,
  minMs: number | null
): T[] => {
  if (!data?.length) return [];
  if (minMs == null) return data;
  return data.filter((point) => {
    const pointMs = new Date(point.date).getTime();
    return Number.isFinite(pointMs) && pointMs >= minMs;
  });
};

const clampRangeFrom = (
  range: {from: Date; to: Date; interval: AnalyticsInterval},
  minMs: number | null
) => {
  if (minMs == null || minMs <= range.from.getTime()) return range;

  const toMs = range.to.getTime();
  const fromMs = Math.min(minMs, toMs);
  return {...range, from: new Date(fromMs)};
};

type StoredMark = {
  hiveId: string | number;
  chartId: ChartId;
  marks: {color: string; markName: string; markValue: number; isDisplayable: boolean}[];
};

type AbsThreshold = {
  hiveId: string | number;
  chartId: ChartId;
  min: number | null;
  max: number | null;
  isDisplayable: boolean;
};
type GainThreshold = {
  hiveId: string | number;
  chartId: ChartId;
  min: number | null;
  max: number | null;
};

const LS_KEYS = {
  marks: 'hiveMarks',
  abs: 'hiveAbsThresholds',
  gain: 'hiveGainThresholds',
};

function readLS<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    return JSON.parse(raw) as T[];
  } catch {
    return [];
  }
}
function writeLS<T>(key: string, arr: T[]) {
  localStorage.setItem(key, JSON.stringify(arr));
}

function getMarksFor(hiveId: string | number, chartId: ChartId): StoredMark['marks'] {
  const list = readLS<StoredMark>(LS_KEYS.marks);
  return (
    list.find((x) => String(x.hiveId) === String(hiveId) && x.chartId === chartId)?.marks ?? []
  );
}
function setMarksFor(hiveId: string | number, chartId: ChartId, marks: StoredMark['marks']) {
  const list = readLS<StoredMark>(LS_KEYS.marks);
  const idx = list.findIndex((x) => String(x.hiveId) === String(hiveId) && x.chartId === chartId);
  if (idx >= 0) list[idx] = {hiveId, chartId, marks};
  else list.push({hiveId, chartId, marks});
  writeLS(LS_KEYS.marks, list);
}

function getAbsFor(hiveId: string | number, chartId: ChartId): AbsThreshold {
  const list = readLS<AbsThreshold>(LS_KEYS.abs);
  return (
    list.find((x) => String(x.hiveId) === String(hiveId) && x.chartId === chartId) ?? {
      hiveId,
      chartId,
      min: null,
      max: null,
      isDisplayable: false,
    }
  );
}
function setAbsFor(
  hiveId: string | number,
  chartId: ChartId,
  data: Omit<AbsThreshold, 'hiveId' | 'chartId'>
) {
  const list = readLS<AbsThreshold>(LS_KEYS.abs);
  const idx = list.findIndex((x) => String(x.hiveId) === String(hiveId) && x.chartId === chartId);
  const rec: AbsThreshold = {hiveId, chartId, ...data};
  if (idx >= 0) list[idx] = rec;
  else list.push(rec);
  writeLS(LS_KEYS.abs, list);
}

function getGainFor(hiveId: string | number, chartId: ChartId): GainThreshold {
  const list = readLS<GainThreshold>(LS_KEYS.gain);
  return (
    list.find((x) => String(x.hiveId) === String(hiveId) && x.chartId === chartId) ?? {
      hiveId,
      chartId,
      min: null,
      max: null,
    }
  );
}
function setGainFor(
  hiveId: string | number,
  chartId: ChartId,
  data: Omit<GainThreshold, 'hiveId' | 'chartId'>
) {
  const list = readLS<GainThreshold>(LS_KEYS.gain);
  const idx = list.findIndex((x) => String(x.hiveId) === String(hiveId) && x.chartId === chartId);
  const rec: GainThreshold = {hiveId, chartId, ...data};
  if (idx >= 0) list[idx] = rec;
  else list.push(rec);
  writeLS(LS_KEYS.gain, list);
}

const DECIMAL_RE = /^-?\d*(?:[.,]\d*)?$/;
const toNum = (s: string) => {
  if (s.trim() === '' || s === '-' || s === ',' || s === '.') return null;
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const getLast24Hours = (data: {date: string; value: number}[]): {date: string; value: number}[] => {
  const now = Date.now();
  const twentyFourHoursAgo = now - 24 * HOUR_MS;
  return data.filter((point) => {
    const pointTime = new Date(point.date).getTime();
    return pointTime >= twentyFourHoursAgo && pointTime <= now;
  });
};

function filterCurrentWindow(
  uiPeriod: UIPeriod,
  data?: {date: string; value: number}[] | null,
  range?: CustomRange | null,
  forceDaily?: boolean
) {
  if (!data || data.length === 0) return data ?? [];

  let processedData = data;

  // Применяем агрегацию в зависимости от периода
  if (forceDaily) {
    if (uiPeriod === 'year') {
      // Для года агрегируем по месяцам
      processedData = aggregateToMonthly(data);
    } else if (uiPeriod === 'month') {
      // Для месяца - по дням
      processedData = aggregateToDaily(data);
    } else if (uiPeriod === 'week') {
      // Для недели также по дням
      processedData = aggregateToDaily(data);
    }
  }

  if (uiPeriod === 'today') {
    return getLast24Hours(processedData);
  }

  if (uiPeriod === 'custom' && range) {
    return processedData.filter((p) => {
      const t = new Date(p.date).getTime();
      return t >= range.start && t <= range.end;
    });
  }

  // Для стандартных периодов фильтруем по времени
  const now = Date.now();
  let start: number;
  let end = now + 1;

  switch (uiPeriod) {
    case 'custom':
      start = startOfDay(new Date()).getTime();
      break;
    case 'yesterday': {
      const yesterdayStart = startOfDay(new Date()).getTime() - DAY_MS;
      start = yesterdayStart;
      end = yesterdayStart + DAY_MS;
      break;
    }
    case 'week':
      start = now - 7 * DAY_MS;
      break;
    case 'month':
      start = now - 30 * DAY_MS;
      break;
    case 'year':
      start = now - 365 * DAY_MS;
      break;
    default:
      start = startOfDay(new Date()).getTime();
      break;
  }

  return processedData.filter((p) => {
    const t = new Date(p.date).getTime();
    return t >= start && t < end;
  });
}

// Функция агрегации по месяцам
const aggregateToMonthly = (data: {date: string; value: number}[]) => {
  if (!data || data.length === 0) return [];

  const monthlyMap = new Map<string, {sum: number; count: number; minDate: string}>();

  data.forEach((point) => {
    const date = new Date(point.date);
    // Ключ в формате "YYYY-MM" для группировки по месяцам
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

    if (!monthlyMap.has(monthKey)) {
      monthlyMap.set(monthKey, {
        sum: 0,
        count: 0,
        minDate: point.date, // сохраняем первую дату в месяце
      });
    }

    const monthData = monthlyMap.get(monthKey)!;
    monthData.sum += point.value;
    monthData.count += 1;
    // Сохраняем минимальную дату в месяце
    if (new Date(point.date) < new Date(monthData.minDate)) {
      monthData.minDate = point.date;
    }
  });

  const result = Array.from(monthlyMap.entries())
    // .map(([monthKey, { sum, count, minDate }]) => ({
    .map(([_, {sum, count, minDate}]) => ({
      date: minDate, // используем минимальную дату в месяце
      value: count > 0 ? sum / count : 0,
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return result;
};

// Функция агрегации по неделям (если нужно)
// const aggregateToWeekly = (data: { date: string; value: number }[]) => {
//     if (!data || data.length === 0) return [];

//     const weeklyMap = new Map<string, { sum: number; count: number; minDate: string }>();

//     data.forEach(point => {
//         const date = new Date(point.date);
//         // Получаем номер недели в году
//         const startOfYear = new Date(date.getFullYear(), 0, 1);
//         const days = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
//         const weekNumber = Math.ceil((days + 1) / 7);
//         const weekKey = `${date.getFullYear()}-W${String(weekNumber).padStart(2, '0')}`;

//         if (!weeklyMap.has(weekKey)) {
//             weeklyMap.set(weekKey, {
//                 sum: 0,
//                 count: 0,
//                 minDate: point.date
//             });
//         }

//         const weekData = weeklyMap.get(weekKey)!;
//         weekData.sum += point.value;
//         weekData.count += 1;
//         if (new Date(point.date) < new Date(weekData.minDate)) {
//             weekData.minDate = point.date;
//         }
//     });

//     const result = Array.from(weeklyMap.entries())
//         // .map(([weekKey, { sum, count, minDate }]) => ({
//         .map(([_, { sum, count, minDate }]) => ({
//             date: minDate,
//             value: count > 0 ? sum / count : 0
//         }))
//         .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

//     return result;
// };

type Props = {
  hideMarksAndThresholds?: boolean;
  isApiary?: boolean;
};

export const HiveChartSection = ({hideMarksAndThresholds, isApiary}: Props) => {
  const params = useParams<{chartId: string; id?: string}>();
  const rawChartId = params.chartId || 'happiness';
  const normalizedChartIdStr = useMemo(() => {
    if (!isApiary) return rawChartId;
    const trimmed = rawChartId.startsWith('apiary-')
      ? rawChartId.slice(7)
      : rawChartId.startsWith('apiary')
        ? rawChartId.slice(6)
        : rawChartId;
    return trimmed as string;
  }, [rawChartId, isApiary]);
  const chartId = (normalizedChartIdStr as ChartId) || 'happiness';
  const hiveId: string = (params.id || '') as string;
  const sensorType = SENSOR_TYPE_BY_CHART[chartId];
  const currentHive = useCurrentHiveStore((s) => s.currentHive);

  const [uiPeriod, setUiPeriod] = useState<UIPeriod>('today');
  const [period, setPeriod] = useState<PeriodKey>('today');
  const [customRange, setCustomRange] = useState<CustomRange | null>(null);
  const [pendingSingleDayStart, setPendingSingleDayStart] = useState<number | null>(null);
  const currentApiary = useApiariesStore((s) => s.currentApiary());
  const apiaryId = useMemo(() => {
    if (!currentApiary) return '';
    return String(
      currentApiary?.id ??
        (currentApiary as any)?.apiaryId ??
        (currentApiary as any)?.apiary?.id ??
        ''
    );
  }, [currentApiary]);
  const [remoteSeries, setRemoteSeries] = useState<
    Partial<Record<'temperature' | 'humidity' | 'co2' | 'weight', {date: string; value: number}[]>>
  >({});
  const [rawServerData, setRawServerData] = useState<
    Partial<Record<'temperature' | 'humidity' | 'co2' | 'weight', any[]>>
  >({});

  const normalizedScale = useMemo<ScaleKey>(() => getScaleForPeriod(period), [period]);

  const metricBundle = (metric: keyof typeof hiveChartsMocks) =>
    hiveChartsMocks[metric]?.[period]?.[normalizedScale];

  const happiness = metricBundle('happiness');
  const strength = hiveChartsMocks['strength']?.[period]?.[normalizedScale];
  const brood = hiveChartsMocks['brood']?.[period]?.[normalizedScale];
  const requestRange = useMemo(
    () => getRequestRange(uiPeriod, customRange),
    [uiPeriod, customRange]
  );
  const hiveCreatedAtMs = useMemo(
    () =>
      !isApiary && currentHive && String(currentHive.id) === String(hiveId)
        ? parseDateMs(currentHive.createdAt)
        : null,
    [currentHive, hiveId, isApiary]
  );
  const minSelectableDate = useMemo(() => {
    if (hiveCreatedAtMs == null) return null;
    const date = new Date(hiveCreatedAtMs);
    date.setHours(0, 0, 0, 0);
    return date;
  }, [hiveCreatedAtMs]);
  const effectiveRequestRange = useMemo(
    () => clampRangeFrom(requestRange, hiveCreatedAtMs),
    [requestRange, hiveCreatedAtMs]
  );
  const effectiveCustomRange = useMemo<CustomRange | null>(() => {
    if (!customRange) return null;
    if (hiveCreatedAtMs == null || hiveCreatedAtMs <= customRange.start) return customRange;
    return {
      start: Math.min(hiveCreatedAtMs, customRange.end),
      end: customRange.end,
    };
  }, [customRange, hiveCreatedAtMs]);

  useEffect(() => {
    if (!apiaryId) return;
    if (!isApiary && !hiveId) return;

    const {from, to, interval} = effectiveRequestRange;
    let cancelled = false;

    const fetchAnalytics = async () => {
      try {
        const response = await $api.get<HiveAnalyticsResponse>('/collector/analytics/summary', {
          params: {
            ...(isApiary ? {} : {hiveId}),
            apiaryId,
            from: from.toISOString(),
            to: to.toISOString(),
            interval,
          },
        });
        if (!cancelled) {
          const rawTemp = response?.data?.temperature ?? [];
          const rawHumid = response?.data?.humidity ?? [];
          const rawCo2 = response?.data?.co2 ?? [];
          const rawWeight = response?.data?.weight ?? [];

          setRawServerData({
            temperature: rawTemp,
            humidity: rawHumid,
            co2: rawCo2,
            weight: rawWeight,
          });

          setRemoteSeries({
            temperature: mapRemoteSeries(rawTemp),
            humidity: mapRemoteSeries(rawHumid),
            co2: mapRemoteSeries(rawCo2),
            weight: mapRemoteSeries(rawWeight),
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[HiveChartSection] /collector/analytics/summary request failed', error);
          setRemoteSeries({});
          setRawServerData({});
        }
      }
    };

    fetchAnalytics();
    return () => {
      cancelled = true;
    };
  }, [hiveId, apiaryId, isApiary, effectiveRequestRange]);

  const happinessWindowData = useMemo(() => {
    const data = happiness?.data ?? [];
    if (uiPeriod === 'week' && data.length > 7) {
      const dailyPoints = new Map<string, {date: string; value: number}>();

      data.forEach((point) => {
        const date = new Date(point.date);
        const dayKey = date.toDateString();

        if (!dailyPoints.has(dayKey)) {
          dailyPoints.set(dayKey, point);
        }
      });

      return Array.from(dailyPoints.values()).slice(0, 7);
    }
    return data;
  }, [happiness?.data, uiPeriod]);
  const happinessData = useMemo(
    () => filterSeriesFrom(happinessWindowData, hiveCreatedAtMs),
    [happinessWindowData, hiveCreatedAtMs]
  );
  const temperatureData = filterCurrentWindow(
    uiPeriod,
    'temperature' in remoteSeries ? (remoteSeries.temperature ?? []) : [],
    effectiveCustomRange,
    true // включаем агрегацию
  );
  const humidityData = filterCurrentWindow(
    uiPeriod,
    'humidity' in remoteSeries ? (remoteSeries.humidity ?? []) : [],
    effectiveCustomRange,
    true
  );
  const co2Data = filterCurrentWindow(
    uiPeriod,
    'co2' in remoteSeries ? (remoteSeries.co2 ?? []) : [],
    effectiveCustomRange,
    true
  );
  const weightData = filterCurrentWindow(
    uiPeriod,
    'weight' in remoteSeries ? (remoteSeries.weight ?? []) : [],
    effectiveCustomRange,
    true
  );
  const strengthData = filterSeriesFrom(
    filterCurrentWindow(uiPeriod, strength?.data ?? [], effectiveCustomRange),
    hiveCreatedAtMs
  );
  const broodData = filterSeriesFrom(
    filterCurrentWindow(uiPeriod, brood?.data ?? [], effectiveCustomRange),
    hiveCreatedAtMs
  );

  const happinessSeries = useMemo(() => toSeries(happinessData), [happinessData]);
  const temperatureSeries = useMemo(() => toSeries(temperatureData), [temperatureData]);
  const humiditySeries = useMemo(() => toSeries(humidityData), [humidityData]);
  const co2Series = useMemo(() => toSeries(co2Data), [co2Data]);
  const weightSeries = useMemo(() => toSeries(weightData), [weightData]);
  const strengthSeries = useMemo(() => toSeries(strengthData), [strengthData]);
  const broodSeries = useMemo(() => toSeries(broodData), [broodData]);

  const customRangeDays = useMemo(() => {
    if (!effectiveCustomRange) return null;
    return Math.max(1, Math.ceil((effectiveCustomRange.end - effectiveCustomRange.start) / DAY_MS));
  }, [effectiveCustomRange]);

  const isSingleDayCustom = uiPeriod === 'custom' && customRangeDays === 1;

  const ticksPeriod: PeriodKey =
    uiPeriod === 'custom'
      ? isSingleDayCustom
        ? 'custom_1_2_days'
        : customRangeDays && customRangeDays <= 7
          ? 'week'
          : 'month'
      : period;

  const rangeOverride = uiPeriod === 'custom' ? effectiveCustomRange : null;
  const chartRangeOverride = useMemo<CustomRange | null>(() => {
    if (rangeOverride) {
      if (!isSingleDayCustom) return rangeOverride;
      const dayStart = new Date(rangeOverride.start);
      dayStart.setHours(0, 0, 0, 0);
      return {start: dayStart.getTime(), end: dayStart.getTime() + DAY_MS};
    }

    if (
      uiPeriod === 'today' &&
      effectiveRequestRange.from.getTime() === requestRange.from.getTime()
    ) {
      return null;
    }

    return {
      start: effectiveRequestRange.from.getTime(),
      end: effectiveRequestRange.to.getTime(),
    };
  }, [rangeOverride, isSingleDayCustom, uiPeriod, requestRange, effectiveRequestRange]);

  const chartXAxisDomain = chartRangeOverride
    ? {min: chartRangeOverride.start, max: chartRangeOverride.end}
    : undefined;

  const happinessXTicks = useMemo(
    () => buildXTicksForSeries(happinessSeries, ticksPeriod, chartRangeOverride),
    [happinessSeries, ticksPeriod, chartRangeOverride]
  );
  const temperatureXTicks = useMemo(
    () => buildXTicksForSeries(temperatureSeries, ticksPeriod, chartRangeOverride),
    [temperatureSeries, ticksPeriod, chartRangeOverride]
  );
  const humidityXTicks = useMemo(
    () => buildXTicksForSeries(humiditySeries, ticksPeriod, chartRangeOverride),
    [humiditySeries, ticksPeriod, chartRangeOverride]
  );
  const co2XTicks = useMemo(
    () => buildXTicksForSeries(co2Series, ticksPeriod, chartRangeOverride),
    [co2Series, ticksPeriod, chartRangeOverride]
  );
  const weightXTicks = useMemo(
    () => buildXTicksForSeries(weightSeries, ticksPeriod, chartRangeOverride),
    [weightSeries, ticksPeriod, chartRangeOverride]
  );
  const stateXTicks = useMemo(
    () =>
      buildXTicksForSeries(
        strengthSeries.length ? strengthSeries : broodSeries,
        ticksPeriod,
        chartRangeOverride
      ),
    [strengthSeries, broodSeries, ticksPeriod, chartRangeOverride]
  );

  const axisTickFormatter = useCallback<XTickFormatter>(
    (value, index, ticks) => formatTickPartsForPeriod(ticksPeriod, value, index, ticks),
    [ticksPeriod]
  );
  const sparklineCurrentDate = useMemo(
    () => (uiPeriod === 'today' ? Date.now() : undefined),
    [uiPeriod]
  );

  const happinessAxisTickFormatter = useCallback<XTickFormatter>(
    (value, index, ticks) => formatTickPartsForPeriod(ticksPeriod, value, index, ticks),
    [ticksPeriod]
  );

  const xDomainByTicks = useCallback(
    (ticks?: number[]) => {
      if (chartXAxisDomain) return chartXAxisDomain;
      if (ticksPeriod !== 'year' || !ticks || ticks.length < 2) return undefined;
      return {min: ticks[0], max: ticks[ticks.length - 1]};
    },
    [chartXAxisDomain, ticksPeriod]
  );

  // const happinessSeries = useMemo(() => toSeries(happinessData), [happinessData]);
  // const temperatureSeries = useMemo(() => toSeries(temperatureData), [temperatureData]);
  // const humiditySeries = useMemo(() => toSeries(humidityData), [humidityData]);
  // const co2Series = useMemo(() => toSeries(co2Data), [co2Data]);
  // const weightSeries = useMemo(() => toSeries(weightData), [weightData]);
  // const strengthSeries = useMemo(() => toSeries(strengthData), [strengthData]);
  // const broodSeries = useMemo(() => toSeries(broodData), [broodData]);

  // const happinessXTicks = useMemo(() => buildXTicksForSeries(happinessSeries, period), [happinessSeries, period]);
  // const temperatureXTicks = useMemo(() => buildXTicksForSeries(temperatureSeries, period), [temperatureSeries, period]);
  // const humidityXTicks = useMemo(() => buildXTicksForSeries(humiditySeries, period), [humiditySeries, period]);
  // const co2XTicks = useMemo(() => buildXTicksForSeries(co2Series, period), [co2Series, period]);
  // const weightXTicks = useMemo(() => buildXTicksForSeries(weightSeries, period), [weightSeries, period]);
  // const stateXTicks = useMemo(
  //     () => buildXTicksForSeries(strengthSeries.length ? strengthSeries : broodSeries, period),
  //     [strengthSeries, broodSeries, period]
  // );

  // const axisTickFormatter = useCallback<XTickFormatter>(
  //     (value) => formatTickPartsForPeriod(period, value),
  //     [period]
  // );

  const [marks, setMarks] = useState<StoredMark['marks']>([]);
  const [abs, setAbs] = useState<AbsThreshold>(() => getAbsFor(hiveId, chartId as ChartId));
  const [gain, setGain] = useState<GainThreshold>(() => getGainFor(hiveId, 'weight'));

  const [absNotify, setAbsNotify] = useState<Record<ChartId, 'once' | 'every'>>({
    [chartId as ChartId]: 'once',
  } as any);
  const [gainNotify, setGainNotify] = useState<'once' | 'every'>('once');
  const [marksNotify, setMarksNotify] = useState<Record<string, 'once' | 'every'>>({});

  const [absText, setAbsText] = useState<{min: string; max: string}>({min: '', max: ''});
  const [gainText, setGainText] = useState<{min: string; max: string}>({min: '', max: ''});
  const [marksText, setMarksText] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    setMarks(getMarksFor(hiveId, chartId as ChartId));
    const a = getAbsFor(hiveId, chartId as ChartId);
    const g = getGainFor(hiveId, 'weight');
    setAbs(a);
    setGain(g);
    setAbsNotify((prev) => ({...prev, [chartId as ChartId]: prev[chartId as ChartId] ?? 'once'}));
    setAbsText({
      min: a.min != null ? String(a.min).replace('.', ',') : '',
      max: a.max != null ? String(a.max).replace('.', ',') : '',
    });
    setGainText({
      min: g.min != null ? String(g.min).replace('.', ',') : '',
      max: g.max != null ? String(g.max).replace('.', ',') : '',
    });
  }, [hiveId, chartId]);

  useEffect(() => {
    setMarksText((prev) => {
      const next = {...prev};
      for (const m of marks) {
        if (next[m.markName] === undefined) {
          next[m.markName] = String(m.markValue).replace('.', ',');
        }
      }
      for (const k of Object.keys(next)) {
        if (!marks.find((m) => m.markName === k)) delete next[k];
      }
      return next;
    });
  }, [marks]);

  const handleExportAnalytics = useCallback(async () => {
    if (!sensorType || !hiveId || isApiary) return;
    try {
      setIsExporting(true);
      console.log('[HiveChartSection] /collector/analytics/export params:', {hiveId, sensorType});
      const response = await $api.get(`/collector/analytics/export`, {
        params: {hiveId, sensorType},
      });
      console.log('[HiveChartSection] export response:', response?.data);
    } catch (error) {
      console.error('[HiveChartSection] export request failed', error);
      toast.error('Не удалось получить данные графика');
    } finally {
      setIsExporting(false);
    }
  }, [hiveId, sensorType, isApiary]);

  useEffect(() => {
    handleExportAnalytics();
  }, [handleExportAnalytics]);

  const marksEnabledValues = useMemo(
    () => marks.filter((m) => m.isDisplayable).map((m) => m.markValue),
    [marks]
  );

  const thresholds = useMemo(() => {
    const arr: number[] = [];
    if (abs.isDisplayable) {
      if (typeof abs.min === 'number') arr.push(abs.min);
      if (typeof abs.max === 'number') arr.push(abs.max);
    }
    return arr;
  }, [abs]);

  const legendMarksFromUser: LegendItem[] = marks
    .filter((m) => m.isDisplayable)
    .map((m) => {
      const colorVar =
        marksColors.find((c) => c.colorId === m.color)?.colorBg ?? '--status-01-muted';
      const css = `var(${colorVar}, ${COLORS.marks})`;
      return {label: m.markName, color: css};
    });

  const absLegend: LegendItem[] =
    abs.isDisplayable && (typeof abs.min === 'number' || typeof abs.max === 'number')
      ? [
          {
            label: chartId === 'weight' ? 'Пороги абсолютной массы' : 'Пороговые значения',
            color: COLORS.threshold,
          },
        ]
      : [];

  const happinessChart: ReactElement | null =
    chartId === 'happiness' && happinessSeries.length ? (
      <DefaultChartWidget
        isActive
        type="column"
        tooltipText={[{title: 'Индекс счастья', dim: 'баллов', isSign: false}]}
        width="100%"
        height={260}
        data={happinessSeries}
        marks={hideMarksAndThresholds ? [] : marksEnabledValues}
        thresholds={hideMarksAndThresholds ? [] : thresholds}
        showAxes
        fillFuture={false}
        periodForFill="day"
        xTicks={happinessXTicks}
        xTickFormatter={happinessAxisTickFormatter}
        xDomain={xDomainByTicks(happinessXTicks)}
        currentDate={sparklineCurrentDate}
        key={`happiness-${period}-${normalizedScale}`}
      />
    ) : null;

  const temperatureChart: ReactElement | null =
    chartId === 'temperature' && temperatureSeries.length ? (
      <DefaultChartWidget
        isActive
        type="sparkline"
        tooltipText={[{title: 'Температура', dim: '°C', isSign: false}]}
        width="100%"
        height={260}
        data={temperatureSeries}
        marks={hideMarksAndThresholds ? [] : marksEnabledValues}
        thresholds={hideMarksAndThresholds ? [] : thresholds}
        showAxes
        fillFuture={false}
        periodForFill="day"
        xTicks={temperatureXTicks}
        xTickFormatter={axisTickFormatter}
        xDomain={xDomainByTicks(temperatureXTicks)}
        currentDate={sparklineCurrentDate}
        key={`temperature-${period}-${normalizedScale}`}
        showPoints
      />
    ) : null;

  const humidityChart: ReactElement | null =
    chartId === 'humidity' && humiditySeries.length ? (
      <DefaultChartWidget
        isActive
        type="sparkline"
        width="100%"
        tooltipText={[{title: 'Влажность', dim: '%', isSign: false}]}
        height={260}
        data={humiditySeries}
        marks={hideMarksAndThresholds ? [] : marksEnabledValues}
        thresholds={hideMarksAndThresholds ? [] : thresholds}
        showAxes
        fillFuture={false}
        periodForFill="day"
        xTicks={humidityXTicks}
        xTickFormatter={axisTickFormatter}
        xDomain={xDomainByTicks(humidityXTicks)}
        currentDate={sparklineCurrentDate}
        key={`humidity-${period}-${normalizedScale}`}
        showPoints
      />
    ) : null;

  const co2Chart: ReactElement | null =
    chartId === 'co2' && co2Series.length ? (
      <DefaultChartWidget
        isActive
        type="sparkline"
        tooltipText={[{title: 'CO₂', dim: 'ppm', isSign: false}]}
        width="100%"
        height={260}
        data={co2Series}
        marks={hideMarksAndThresholds ? [] : marksEnabledValues}
        thresholds={hideMarksAndThresholds ? [] : thresholds}
        showAxes
        fillFuture={false}
        periodForFill="day"
        xTicks={co2XTicks}
        xTickFormatter={axisTickFormatter}
        xDomain={xDomainByTicks(co2XTicks)}
        currentDate={sparklineCurrentDate}
        key={`co2-${period}-${normalizedScale}`}
        showPoints
      />
    ) : null;

  const weightChart: ReactElement | null =
    chartId === 'weight' && weightSeries.length ? (
      <DefaultChartWidget
        isActive
        type="column"
        tooltipText={[
          {title: 'Масса улья', dim: 'кг', isSign: false},
          {title: 'Привес', dim: 'кг', isSign: true},
        ]}
        width="100%"
        height={260}
        data={weightSeries}
        marks={hideMarksAndThresholds ? [] : marksEnabledValues}
        thresholds={hideMarksAndThresholds ? [] : thresholds}
        showAxes
        fillFuture={false}
        periodForFill="day"
        showDiff
        xTicks={weightXTicks}
        xTickFormatter={axisTickFormatter}
        xDomain={xDomainByTicks(weightXTicks)}
        currentDate={sparklineCurrentDate}
        key={`weight-${period}-${normalizedScale}`}
      />
    ) : null;

  const stateChart: ReactElement | null =
    chartId === 'state' && (strengthSeries.length || broodSeries.length) ? (
      <DefaultChartWidget
        isActive
        type="column2"
        width="100%"
        tooltipText={[
          {title: 'Сила', dim: '', isSign: false},
          {title: 'Расплод', dim: '', isSign: false},
        ]}
        height={260}
        data={strengthSeries}
        data2={broodSeries}
        marks={hideMarksAndThresholds ? [] : marksEnabledValues}
        thresholds={hideMarksAndThresholds ? [] : thresholds}
        showAxes
        fillFuture={false}
        periodForFill="day"
        seriesLabels={{first: 'Сила', second: 'Расплод'}}
        xTicks={stateXTicks}
        xTickFormatter={axisTickFormatter}
        xDomain={xDomainByTicks(stateXTicks)}
        currentDate={sparklineCurrentDate}
        key={`state-${period}-${normalizedScale}`}
      />
    ) : null;

  const lastUpdateMap: Partial<Record<ChartId, string>> = {
    happiness: lastTimeStr(happinessData),
    temperature: lastTimeStr(temperatureData),
    humidity: lastTimeStr(humidityData),
    co2: lastTimeStr(co2Data),
    weight: lastTimeStr(weightData),
    state: lastTimeStr((strengthData?.length ? strengthData : broodData) ?? []),
  };

  const avgMap: Record<
    ChartId,
    {name: string; value: number | string; unit: string; status?: 'green'}[]
  > = {
    happiness: [
      {name: 'В среднем', value: avgValue(happinessData), unit: 'баллов', status: 'green'},
    ],
    temperature: [
      {name: 'В среднем', value: avgValue(temperatureData), unit: '°C', status: 'green'},
    ],
    humidity: [
      {name: 'В среднем', value: Math.round(avgValue(humidityData)), unit: '%', status: 'green'},
    ],
    co2: [{name: 'В среднем', value: Math.round(avgValue(co2Data)), unit: 'ppm', status: 'green'}],
    weight: [
      {name: 'Масса в среднем', value: avgValue(weightData), unit: 'кг', status: 'green'},
      {
        name: 'Привес за период',
        value: (() => {
          const d = periodDelta(weightData);
          return d > 0 ? `+${d}` : `${d}`;
        })(),
        unit: 'кг',
        status: 'green',
      },
    ],
    state: [
      {name: 'Сила', value: avgValue(strengthData), unit: 'баллов', status: 'green'},
      {name: 'Расплод', value: avgValue(broodData), unit: 'баллов', status: 'green'},
    ],
  };

  const titleMap: Record<ChartId, string> = {
    happiness: 'Индекс счастья',
    temperature: 'Температура',
    humidity: 'Влажность',
    co2: 'CO₂',
    weight: 'Масса улья',
    state: 'Состояние семьи',
  };

  const iconMap: Record<ChartId, ReactElement> = {
    happiness: <HappyChapter />,
    temperature: <TemperatureChapter />,
    humidity: <HumidityChapter />,
    co2: <Co2Chapter />,
    weight: <WeightChapter />,
    state: <StrengthChapter />,
  };

  const legendMarksSafe = hideMarksAndThresholds ? [] : legendMarksFromUser;
  const absLegendSafe = hideMarksAndThresholds ? [] : absLegend;

  const happinessLegend: LegendItem[] = [
    legendSeries('Индекс счастья', COLORS.bar),
    ...legendMarksSafe,
    ...absLegendSafe,
  ];
  const temperatureLegend: LegendItem[] = [
    legendSeries('Температура', COLORS.line),
    ...legendMarksSafe,
    ...absLegendSafe,
  ];
  const humidityLegend: LegendItem[] = [
    legendSeries('Влажность', COLORS.line),
    ...legendMarksSafe,
    ...absLegendSafe,
  ];
  const co2Legend: LegendItem[] = [
    legendSeries('CO₂', COLORS.line),
    ...legendMarksSafe,
    ...absLegendSafe,
  ];
  const weightLegend: LegendItem[] = [
    legendSeries('Абсолютная масса', COLORS.bar),
    {label: 'Положительный привес', color: COLORS.diffPos},
    {label: 'Отрицательный привес', color: COLORS.diffNeg},
    ...legendMarksSafe,
    ...(absLegendSafe.length ? [{label: 'Пороги абсолютной массы', color: COLORS.threshold}] : []),
  ];
  const familyLegend: LegendItem[] = [
    legendSeries('Сила', COLORS.bar),
    legendSeries('Расплод', COLORS.bar2),
    ...legendMarksSafe,
    ...absLegendSafe,
  ];

  const legendMap: Partial<Record<ChartId, LegendItem[]>> = {
    happiness: happinessLegend,
    temperature: temperatureLegend,
    humidity: humidityLegend,
    co2: co2Legend,
    weight: weightLegend,
    state: familyLegend,
  };

  const chartNode =
    happinessChart ||
    temperatureChart ||
    humidityChart ||
    co2Chart ||
    weightChart ||
    stateChart ||
    null;

  const hasServerDataMap: Record<ChartId, boolean> = {
    happiness: false, // happiness не приходит с сервера
    temperature:
      'temperature' in rawServerData ? (rawServerData.temperature?.length ?? 0) > 0 : false,
    humidity: 'humidity' in rawServerData ? (rawServerData.humidity?.length ?? 0) > 0 : false,
    co2: 'co2' in rawServerData ? (rawServerData.co2?.length ?? 0) > 0 : false,
    weight: 'weight' in rawServerData ? (rawServerData.weight?.length ?? 0) > 0 : false,
    state: false, // state не приходит с сервера
  };

  const hasDataMap: Record<ChartId, boolean> = {
    happiness: happinessSeries.length > 0,
    temperature: temperatureSeries.length > 0,
    humidity: humiditySeries.length > 0,
    co2: co2Series.length > 0,
    weight: weightSeries.length > 0,
    state: strengthSeries.length > 0 || broodSeries.length > 0,
  };

  const chartContent =
    chartNode ??
    (hasServerDataMap[chartId as ChartId] ? (
      <div
        style={{
          minHeight: 240,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--fg-muted, rgba(16, 24, 40, 0.55))',
          fontSize: 16,
          border: '1px dashed var(--border-soft, rgba(88,93,105,0.2))',
          borderRadius: 16,
          padding: 24,
          textAlign: 'center',
        }}
      >
        Нет данных для выбранного диапазона
      </div>
    ) : null);

  const setOutletColor = useOutletColorStore((s) => s.setColor);
  const resetOutletColor = useOutletColorStore((s) => s.resetColor);

  useEffect(() => {
    setOutletColor(MHZ_COLORS.BG.PAGE_FILLED);
    return () => {
      resetOutletColor();
    };
  }, []);

  const [isOpenMarkAction, setIsOpenMarkAction] = useState<string>();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editTargetName, setEditTargetName] = useState<string | null>(null);

  const updateIsOpenMarkAction = (markId: string) => {
    if (isOpenMarkAction === markId) setIsOpenMarkAction('');
    else setIsOpenMarkAction(markId);
  };

  const uniqueMarkName = useCallback(
    (base: string, skipName?: string) => {
      const existing = new Set(marks.map((m) => m.markName));
      if (skipName) existing.delete(skipName);
      const name = base.trim();
      if (!existing.has(name)) return name;
      let i = 1;
      while (existing.has(`${name} (${i})`)) i++;
      return `${name} (${i})`;
    },
    [marks]
  );

  const openCreate = () => {
    setEditTargetName(null);
    setIsEditOpen(true);
  };
  const openEdit = (name: string) => {
    setEditTargetName(name);
    setIsEditOpen(true);
  };

  const initialForEdit: Partial<HiveMarkEditPayload> | undefined = useMemo(() => {
    if (!editTargetName) return undefined;
    const m = marks.find((x) => x.markName === editTargetName);
    if (!m) return undefined;
    const varName = marksColors.find((c) => c.colorId === m.color)?.colorBg ?? '--status-01-muted';
    return {markName: m.markName, color: `var(${varName})`};
  }, [editTargetName, marks]);

  const saveMark = (payload: HiveMarkEditPayload) => {
    const toColorId = (colorBg: string): string => {
      const val = colorBg.startsWith('var(') ? colorBg.slice(4, -1).replace(/\)$/, '') : colorBg;
      const found = marksColors.find((c) => c.colorId === val);
      return found?.colorId ?? val;
    };
    if (editTargetName) {
      const upd = marks.map((m) =>
        m.markName === editTargetName
          ? {
              ...m,
              markName: uniqueMarkName(payload.markName || editTargetName, editTargetName),
              color: toColorId(payload.color),
            }
          : m
      );
      setMarks(upd);
      setMarksFor(hiveId, chartId as ChartId, upd);
    } else {
      const name = uniqueMarkName(payload.markName || 'Метка');
      const next = [
        ...marks,
        {
          color: toColorId(payload.color),
          markName: name,
          markValue: 0,
          isDisplayable: true,
        },
      ];
      setMarks(next);
      setMarksFor(hiveId, chartId as ChartId, next);
    }
    setIsEditOpen(false);
    setEditTargetName(null);
  };

  const duplicateMark = (name: string) => {
    const src = marks.find((m) => m.markName === name);
    if (!src) return;
    const copyName = uniqueMarkName(`${name}`);
    const next = [
      ...marks,
      {
        ...src,
        markName: copyName,
      },
    ];
    setMarks(next);
    setMarksFor(hiveId, chartId as ChartId, next);
  };

  const deleteMark = (name: string) => {
    const next = marks.filter((m) => m.markName !== name);
    setMarks(next);
    setMarksFor(hiveId, chartId as ChartId, next);
    setMarksNotify((prev) => {
      const cp = {...prev};
      delete cp[name];
      return cp;
    });
  };

  const absTitleMap: Record<ChartId, string> = {
    weight: 'Пороги абсолютной массы',
    temperature: 'Пороговые значения температуры',
    humidity: 'Пороговые значения влажности',
    co2: 'Пороговые значения CO₂',
    happiness: 'Пороговые значения индекса',
    state: 'Пороговые значения состояния',
  };

  return (
    <>
      <ChartContainer>
        <ChartHeader>
          <SegmentedControl
            size="m"
            variant="secondary"
            value={uiPeriod}
            onChange={(val) => {
              const key = val as UIPeriod;
              setUiPeriod(key);
              if (key !== 'custom') {
                setPendingSingleDayStart(null);
                setCustomRange(null);
                setPeriod(key as PeriodKey);
              }
            }}
          >
            <Segment index="today" label="День" />
            {/*<Segment index="yesterday" label="Вчера" />*/}
            <Segment index="week" label="Неделя" />
            <Segment index="month" label="Месяц" />
            <Segment index="year" label="Год" />
            <DateInputChartWrapper className={uiPeriod === 'custom' ? 'active' : ''}>
              <InputDate
                key={`period-${uiPeriod}`}
                placeholder="Период"
                ref={withMask(['99.99.9999', '99.99.9999 - 99.99.9999'])}
                maxDate={new Date()}
                minDate={minSelectableDate ?? undefined}
                activeDate={
                  customRange
                    ? new Date(customRange.start)
                    : pendingSingleDayStart
                      ? new Date(pendingSingleDayStart)
                      : undefined
                }
                secondDate={
                  customRange ? new Date(new Date(customRange.end).setHours(0, 0, 0, 0)) : undefined
                }
                isRange
                onChange={(start, end) => {
                  const s = start instanceof Date ? start : null;
                  const e = end instanceof Date ? end : null;
                  if (!s) {
                    setPendingSingleDayStart(null);
                    setCustomRange(null);
                    return;
                  }

                  const sDay = new Date(s);
                  sDay.setHours(0, 0, 0, 0);
                  const sDayMs = sDay.getTime();

                  if (s && e) {
                    setPendingSingleDayStart(null);
                    const ms = e.getTime() - s.getTime();
                    const days = Math.max(1, Math.ceil(ms / (24 * 3600_000)));
                    const bucket = classifyCustomRangeDays(days);
                    setCustomRange(normalizeRange(s, e));
                    setUiPeriod('custom');
                    setPeriod(bucket);
                  } else {
                    // Second click on the same date -> force one full day range.
                    if (pendingSingleDayStart !== null && pendingSingleDayStart === sDayMs) {
                      const dayEnd = new Date(sDayMs);
                      dayEnd.setHours(23, 59, 59, 999);
                      const bucket = classifyCustomRangeDays(1);
                      setPendingSingleDayStart(null);
                      setCustomRange({start: sDayMs, end: dayEnd.getTime()});
                      setUiPeriod('custom');
                      setPeriod(bucket);
                      return;
                    }
                    // Keep previous behavior for first click: clear selected range,
                    // but preserve clicked day as pending to avoid visual reset.
                    setCustomRange(null);
                    setPendingSingleDayStart(sDayMs);
                  }
                }}
                onBlur={() => {}}
              />
            </DateInputChartWrapper>
          </SegmentedControl>

          {!isApiary && sensorType && hiveId ? (
            <UnionButton
              type="button"
              onClick={handleExportAnalytics}
              disabled={isExporting}
              style={{marginLeft: 'auto', flexShrink: 0, opacity: 0}}
            >
              <UnionIcon />
              <span>{isExporting ? 'Выгрузка...' : 'Выгрузить'}</span>
            </UnionButton>
          ) : null}
        </ChartHeader>
        <ChartRow>
          <ChartWrapper>
            <AnalyticsCard
              id={chartId as ChartId}
              icon={iconMap[chartId as ChartId]}
              title={titleMap[chartId as ChartId]}
              lastUpdate={lastUpdateMap[chartId as ChartId] ?? ''}
              link=""
              averages={avgMap[chartId as ChartId]}
              legend={legendMap[chartId as ChartId]}
              hasData={hasDataMap[chartId as ChartId]}
              hasServerData={hasServerDataMap[chartId as ChartId]}
            >
              {chartContent}
            </AnalyticsCard>
          </ChartWrapper>
          {!hideMarksAndThresholds && (
            <ChartAbsoluteCol>
              <ChartAbsoluteBlock>
                <ChartAbsoluteHeader>
                  <h2>{absTitleMap[chartId as ChartId]}</h2>
                  <ChartAbsoluteMinMaxRow>
                    <ChartAbsoluteMinMaxCol>
                      <h3>Мин.</h3>
                      <Input
                        size={'m'}
                        value={absText.min}
                        inputMode="decimal"
                        onChange={(e) => {
                          const v = e.target.value.replace('.', ',');
                          if (!DECIMAL_RE.test(v)) return;
                          setAbsText((t) => ({...t, min: v}));
                          const num = toNum(v);
                          const next = {...abs, min: num};
                          setAbs(next);
                          setAbsFor(hiveId, chartId as ChartId, next);
                        }}
                        onBlur={() => {
                          const num = toNum(absText.min);
                          setAbsText((t) => ({
                            ...t,
                            min: num != null ? String(num).replace('.', ',') : '',
                          }));
                        }}
                      />
                    </ChartAbsoluteMinMaxCol>
                    <ChartAbsoluteMinMaxCol>
                      <h3>Макс.</h3>
                      <Input
                        size={'m'}
                        value={absText.max}
                        inputMode="decimal"
                        onChange={(e) => {
                          const v = e.target.value.replace('.', ',');
                          if (!DECIMAL_RE.test(v)) return;
                          setAbsText((t) => ({...t, max: v}));
                          const num = toNum(v);
                          const next = {...abs, max: num};
                          setAbs(next);
                          setAbsFor(hiveId, chartId as ChartId, next);
                        }}
                        onBlur={() => {
                          const num = toNum(absText.max);
                          setAbsText((t) => ({
                            ...t,
                            max: num != null ? String(num).replace('.', ',') : '',
                          }));
                        }}
                      />
                    </ChartAbsoluteMinMaxCol>
                  </ChartAbsoluteMinMaxRow>
                  <ChartSwitchRow>
                    <Switch
                      key={`abs-${String(hiveId)}-${String(chartId)}`}
                      label="Показать на графике"
                      labelPosition="left"
                      checked={abs.isDisplayable}
                      onChange={(value) => {
                        const next = {...abs, isDisplayable: value};
                        setAbs(next);
                        setAbsFor(hiveId, chartId as ChartId, next);
                      }}
                    />
                  </ChartSwitchRow>
                </ChartAbsoluteHeader>
                <ChartAbsoluteFooter>
                  <p>Уведомлять</p>
                  <Select
                    defaultValue={absNotify[chartId as ChartId] ?? 'once'}
                    onChange={(v) =>
                      setAbsNotify((prev) => ({
                        ...prev,
                        [chartId as ChartId]: v as 'once' | 'every',
                      }))
                    }
                    items={[
                      {value: 'Однократно', key: 'once'},
                      {value: 'Каждый раз', key: 'every'},
                    ]}
                  />
                </ChartAbsoluteFooter>
              </ChartAbsoluteBlock>
              {chartId === 'weight' ? (
                <ChartAbsoluteBlock>
                  <ChartAbsoluteHeader>
                    <h2>Пороги привеса</h2>
                    <ChartAbsoluteMinMaxRow>
                      <ChartAbsoluteMinMaxCol>
                        <h3>Мин.</h3>
                        <Input
                          size={'m'}
                          value={gainText.min}
                          inputMode="decimal"
                          onChange={(e) => {
                            const v = e.target.value.replace('.', ',');
                            if (!DECIMAL_RE.test(v)) return;
                            setGainText((t) => ({...t, min: v}));
                            const num = toNum(v);
                            const next = {...gain, min: num};
                            setGain(next);
                            setGainFor(hiveId, 'weight', next);
                          }}
                          onBlur={() => {
                            const num = toNum(gainText.min);
                            setGainText((t) => ({
                              ...t,
                              min: num != null ? String(num).replace('.', ',') : '',
                            }));
                          }}
                        />
                      </ChartAbsoluteMinMaxCol>
                      <ChartAbsoluteMinMaxCol>
                        <h3>Макс.</h3>
                        <Input
                          size={'m'}
                          value={gainText.max}
                          inputMode="decimal"
                          onChange={(e) => {
                            const v = e.target.value.replace('.', ',');
                            if (!DECIMAL_RE.test(v)) return;
                            setGainText((t) => ({...t, max: v}));
                            const num = toNum(v);
                            const next = {...gain, max: num};
                            setGain(next);
                            setGainFor(hiveId, 'weight', next);
                          }}
                          onBlur={() => {
                            const num = toNum(gainText.max);
                            setGainText((t) => ({
                              ...t,
                              max: num != null ? String(num).replace('.', ',') : '',
                            }));
                          }}
                        />
                      </ChartAbsoluteMinMaxCol>
                    </ChartAbsoluteMinMaxRow>
                  </ChartAbsoluteHeader>
                  <ChartAbsoluteFooter>
                    <p>Уведомлять</p>
                    <Select
                      defaultValue={gainNotify}
                      onChange={(v) => setGainNotify(v as 'once' | 'every')}
                      items={[
                        {value: 'Однократно', key: 'once'},
                        {value: 'Каждый раз', key: 'every'},
                      ]}
                    />
                  </ChartAbsoluteFooter>
                </ChartAbsoluteBlock>
              ) : null}
            </ChartAbsoluteCol>
          )}
        </ChartRow>
        {!hideMarksAndThresholds && (
          <ChartMarksContainer>
            <ChartMarksTitleRow>
              <h2>Пользовательские метки</h2>
            </ChartMarksTitleRow>
            <ChartMarkWrapper>
              {marks.map((m) => (
                <ChartMarkItem key={m.markName}>
                  <ChartMarkItemHeader>
                    <ChartMarkItemTitleRow>
                      <ChartMarkItemColor
                        style={{
                          background: `var(${marksColors.find((c) => c.colorId === m.color)?.colorBg ?? '--status-01-muted'})`,
                        }}
                      />
                      {m.markName}
                      <ChartMarkItemMore>
                        <MoreIcon onClick={() => updateIsOpenMarkAction(m.markName)} />
                        {isOpenMarkAction === m.markName && (
                          <HiveMarkActions
                            onEdit={() => openEdit(m.markName)}
                            onDuplicate={() => duplicateMark(m.markName)}
                            onDelete={() => deleteMark(m.markName)}
                          />
                        )}
                      </ChartMarkItemMore>
                    </ChartMarkItemTitleRow>

                    <ChartMarkInputDesc>Цель</ChartMarkInputDesc>
                    <ChartMarkInput>
                      <Input
                        size={'m'}
                        value={marksText[m.markName] ?? ''}
                        inputMode="decimal"
                        onChange={(e) => {
                          const v = e.target.value.replace('.', ',');
                          if (!DECIMAL_RE.test(v)) return;
                          setMarksText((prev) => ({...prev, [m.markName]: v}));
                          const num = toNum(v);
                          setMarks((prev) => {
                            const next = prev.map((x) =>
                              x.markName === m.markName ? {...x, markValue: num ?? x.markValue} : x
                            );
                            setMarksFor(hiveId, chartId as ChartId, next);
                            return next;
                          });
                        }}
                        onBlur={() => {
                          const num = toNum(marksText[m.markName] ?? '');
                          setMarksText((prev) => ({
                            ...prev,
                            [m.markName]: num != null ? String(num).replace('.', ',') : '',
                          }));
                        }}
                      />
                    </ChartMarkInput>

                    <ChartSwitchRow>
                      <label
                        htmlFor={`mark-checkbox-${String(hiveId)}-${String(chartId)}-${m.markName}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontSize: '14px',
                        }}
                      >
                        <input
                          id={`mark-checkbox-${String(hiveId)}-${String(chartId)}-${m.markName}`}
                          type="checkbox"
                          checked={m.isDisplayable}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setMarks((prev) => {
                              const next = prev.map((x) =>
                                x.markName === m.markName ? {...x, isDisplayable: checked} : x
                              );
                              setMarksFor(hiveId, chartId as ChartId, next);
                              return next;
                            });
                          }}
                          style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '4px',
                            border: '1px solid var(--border-default, #ccc)',
                            accentColor: 'var(--accent-default, #6B8AFF)',
                            cursor: 'pointer',
                          }}
                        />
                        Показать на графике
                      </label>
                    </ChartSwitchRow>
                  </ChartMarkItemHeader>

                  <ChartMarkFooter>
                    <p>Уведомлять</p>
                    <Select
                      defaultValue={marksNotify[m.markName] ?? 'once'}
                      onChange={(v) =>
                        setMarksNotify((prev) => ({...prev, [m.markName]: v as 'once' | 'every'}))
                      }
                      items={[
                        {value: 'Однократно', key: 'once'},
                        {value: 'Каждый раз', key: 'every'},
                      ]}
                    />
                  </ChartMarkFooter>
                </ChartMarkItem>
              ))}
              <ChartMarkCreate onClick={openCreate}>
                <AddLarge />
                <p>Добавить метку</p>
              </ChartMarkCreate>
            </ChartMarkWrapper>
          </ChartMarksContainer>
        )}
      </ChartContainer>
      {!hideMarksAndThresholds && (
        <HiveMarkEdit
          isOpen={isEditOpen}
          mode={editTargetName ? 'edit' : 'create'}
          initial={initialForEdit}
          onClose={() => {
            setIsEditOpen(false);
            setEditTargetName(null);
          }}
          onSave={saveMark}
        />
      )}
    </>
  );
};
